import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Sprint 3 — palette open/mode state, mounted once at AppShell level and
 * mirrored on the Sprint 9a {@link RightRailContext} pattern. The global
 * Cmd+K / Cmd+/ handler in AppShell drives `setOpen` / `openCheatsheet`; the
 * TopBar `<CmdKHint>` and any in-app affordance call the same hook.
 */

export type CommandPaletteMode = "default" | "cheatsheet";

interface CommandPaletteContextValue {
  open: boolean;
  mode: CommandPaletteMode;
  setOpen: (open: boolean) => void;
  /** Toggle open; resets to default mode on open. */
  toggle: () => void;
  /** Open directly in cheatsheet mode (Cmd+/). */
  openCheatsheet: () => void;
  setMode: (mode: CommandPaletteMode) => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpenState] = useState(false);
  const [mode, setMode] = useState<CommandPaletteMode>("default");

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    if (!next) setMode("default");
  }, []);

  const toggle = useCallback(() => {
    setOpenState((cur) => {
      const next = !cur;
      if (next) setMode("default");
      return next;
    });
  }, []);

  const openCheatsheet = useCallback(() => {
    setMode("cheatsheet");
    setOpenState(true);
  }, []);

  const value = useMemo<CommandPaletteContextValue>(
    () => ({ open, mode, setOpen, toggle, openCheatsheet, setMode }),
    [open, mode, setOpen, toggle, openCheatsheet],
  );

  return (
    <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>
  );
}

export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error("useCommandPalette must be used within a CommandPaletteProvider");
  }
  return ctx;
}
